import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, WORKER_API_BASE } from '../lib/api';
import { useToast } from '../components/Toast';
import { tr } from '../lib/i18nHelpers';
import { filterLeafAccounts, NOT_APPLICABLE, isTemporaryAccount } from '../lib/coa-hierarchy';
import { isMissingCoa } from '../lib/coa-flag';
import { ocrSourceLabel, ocrSourceBadgeClass } from '../lib/ocrSourceLabel';
import { rowPostingView } from '../lib/statementRowPosting';
import { advanceReviewQueue, exitDest } from '../lib/ocrHighlight';
import { stageOf } from '../lib/lifecycleStepper';
import { postingStatus, type StatementPostingSummary } from '../lib/statementPostingStatus';
import { withPdfPage } from '../lib/pdfPage';
import LifecycleStepper from '../components/LifecycleStepper';
import LifecycleDrilldown from '../components/LifecycleDrilldown';
import DocSendBackMenu from '../components/DocSendBackMenu';

// Money formatter — always 2 decimals with thousand separators
const money = (v: number | null | undefined): string => {
  if (v == null || v === undefined || isNaN(Number(v))) return '';
  return Number(v).toLocaleString('en-HK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Parse user-typed money back to a number (strip commas + non-numeric)
const parseMoney = (s: string): number | null => {
  if (s == null || String(s).trim() === '') return null;
  const cleaned = String(s).replace(/,/g, '').replace(/[^\d.\-]/g, '');
  if (cleaned === '' || cleaned === '-' || cleaned === '.') return null;
  const n = parseFloat(cleaned);
  return isNaN(n) ? null : n;
};

// Money input that DISPLAYS commas + 2 decimals when not focused,
// and shows the raw editable number while focused.
// Fixes Lily issues #3 and #11 (decimals/commas hidden until you click).
function MoneyInput({
  value, onChange, className = '', placeholder,
}: {
  value: number | null | undefined;
  onChange: (v: number | null) => void;
  className?: string;
  placeholder?: string;
}) {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState('');
  const shown = focused
    ? draft
    : (value == null || value === undefined || isNaN(Number(value)) ? '' : money(value));
  return (
    <input
      type="text"
      inputMode="decimal"
      value={shown}
      placeholder={placeholder}
      onFocus={() => {
        setFocused(true);
        setDraft(value == null || isNaN(Number(value)) ? '' : String(value));
      }}
      onChange={e => {
        setDraft(e.target.value);
        onChange(parseMoney(e.target.value));
      }}
      onBlur={() => setFocused(false)}
      className={className}
    />
  );
}

interface Transaction {
  id: string;
  transaction_date: string;
  description: string;
  deposit_amount: number;
  withdrawal_amount: number;
  balance: number | null;
  reference?: string | null;
  payee_payer?: string | null;
  cheque_no?: string | null;
  account_type?: string | null;
  account_code?: string | null;
  match_status?: string | null;
  // Dr/Cr posting lines (bank voucher + invoice-payment voucher) returned by
  // GET /bank-statements/:id — rendered as Dr/Cr pills in the COA column,
  // mirroring the Bank Statements transaction record.
  posting?: { lines: { account_code: string; account_name?: string; debit: number; credit: number }[] } | null;
  payment_posting?: { entry_number?: string; lines: { account_code: string; account_name?: string; debit: number; credit: number }[] } | null;
  // Invoice/card link fields — read-only on this screen (returned by
  // GET /bank-statements/:id; linking stays on the Bank Statements page).
  invoice_id?: string | null;
  match_confidence?: string | null;
  invoice_number?: string | null;
  invoice_total?: number | null;
  invoice_status?: string | null;
  card_statement_id?: string | null;
  card_issuer?: string | null;
  linked_invoices?: { invoice_id: string; invoice_number: string; allocated_amount: number | null }[] | null;
}

interface StatementWithTx {
  id: string;
  file_name?: string;
  bank_name?: string;
  account_number?: string;
  branch?: string;
  currency?: string;
  account_type?: string;
  /** Bank GL account the statement posts against (Dr/Cr bank side). */
  account_code?: string | null;
  is_reconciled?: boolean;
  statement_year?: number;
  statement_month?: number;
  period_start?: string;
  period_end?: string;
  opening_balance?: number;
  closing_balance?: number;
  status?: string;
  ocr_source?: string;
  /** 1-based page where this statement section starts inside the source PDF. */
  source_page?: number | null;
  transactions?: Transaction[];
  posting_summary?: StatementPostingSummary;
}

export default function BankStatementReview() {
  const { i18n } = useTranslation();
  const toast = useToast();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isDuplicate = searchParams.get('is_duplicate') === '1';
  const dupStatus = searchParams.get('dup_status');
  const dupBlocked = searchParams.get('dup_blocked') === '1';
  // Entered from File Storage? Save/discard lands back there.
  const fromParam = searchParams.get('from');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: stmt, isLoading } = useQuery<StatementWithTx>({
    queryKey: ['bank-statement', id],
    queryFn: () => api(`/bank-statements/${id}`),
    enabled: !!id,
  });

  // Canonical lifecycle position: { object: { stage, stage_since, flags, … } }.
  // Derived doc stages (booked/posted/reconciled) ride on this — never
  // re-derived here. Null/absent → falls back to the raw row status lane.
  const { data: lc } = useQuery({
    queryKey: ['bank-lifecycle', id],
    queryFn: () => api(`/lifecycle/bank_statement/${id}`),
    enabled: !!id,
  }) as { data?: { object?: { stage?: string; stage_since?: string | null } } | undefined };

  // COA options for per-transaction account assignment/override
  interface CoaOption { account_code: string; account_name: string }
  const { data: coaResp } = useQuery<{ data: CoaOption[] }>({
    queryKey: ['coa-options'],
    queryFn: () => api('/bookkeeping/accounts'),
  });
  const coaOptions = useMemo(() => {
    const all = (coaResp?.data || []);
    // Parents with children are not postable — hide them (shared zero-stripped-
    // stem rule, same as the bank statements list pickers + backend PATCH guard).
    return filterLeafAccounts(all)
      .slice()
      .sort((x: any, y: any) => x.account_code.localeCompare(y.account_code));
  }, [coaResp]);

  // Local edit state
  const [headerEdits, setHeaderEdits] = useState<Partial<StatementWithTx>>({});
  const [txEdits, setTxEdits] = useState<Record<string, Partial<Transaction>>>({});
  const [deletedTxIds, setDeletedTxIds] = useState<Set<string>>(new Set());
  // Track whether the user has manually typed a closing balance.
  // While false, the closing balance auto-follows the computed running total
  // (Lily #4: accountants edit line items first, the total should follow).
  const [closingManuallyEdited, setClosingManuallyEdited] = useState(false);
  // Rows added manually via "Add Row" (used when OCR failed to read the file).
  const [localRows, setLocalRows] = useState<Transaction[]>([]);
  // Not-Applicable rows whose Dr/Cr pickers the user revealed (Bank Statements
  // pattern: N/A rows show a chip first; clicking it exposes the pickers).
  const [naRevealIds, setNaRevealIds] = useState<Set<string>>(new Set());
  // Task 16: lineage drill-down modal for this statement.
  const [drillOpen, setDrillOpen] = useState(false);
  // Guard against double-submission — true for the ENTIRE save-and-confirm pipeline
  const [isSaving, setIsSaving] = useState(false);
  // Reset ALL local state when navigating to a different review (React Router reuses component)
  // This fixes the same class of bug as the previous isSaving-only fix:
  // stale pdfUrl/pdfError/edits leaking across queue items causes blank PDF panes and dead "Loading…" states.
  useEffect(() => {
    setIsSaving(false);
    setPdfUrl(null);
    setPdfError(null);
    setHeaderEdits({});
    setTxEdits({});
    setDeletedTxIds(new Set());
    setLocalRows([]);
    setNaRevealIds(new Set());
    setClosingManuallyEdited(false);
  }, [id]);

  // PDF blob URL (loaded with auth)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);

  // ── Resizable split: PDF pane width (%) on lg+ screens ──
  // The date/description columns used to clip at a fixed 50/50 split; the
  // user can now drag the divider (persisted across visits). Small screens
  // keep the stacked layout.
  // Drag uses POINTER CAPTURE on the divider: releasing the mouse over the
  // PDF iframe never reaches a plain window mouseup (the iframe swallows
  // it), which left the drag stuck — the divider kept following the mouse
  // after release. Capture retargets every move/up to the divider instead.
  const splitRef = useRef<HTMLDivElement | null>(null);
  const [pdfPct, setPdfPct] = useState<number>(() => {
    const saved = Number(localStorage.getItem('bs-review-split'));
    return saved >= 20 && saved <= 80 ? saved : 50;
  });
  const applyPointerPct = (clientX: number) => {
    const rect = splitRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPdfPct(Math.min(80, Math.max(20, pct)));
  };
  // Idempotent drag-end: safe to fire from both pointerup and lostpointercapture.
  const endDividerDrag = (el: HTMLElement, pointerId: number) => {
    if (el.hasPointerCapture(pointerId)) el.releasePointerCapture(pointerId);
    document.body.classList.remove('select-none', 'cursor-col-resize');
    setPdfPct(p => {
      localStorage.setItem('bs-review-split', String(Math.round(p)));
      return p;
    });
  };
  const onDividerPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    document.body.classList.add('select-none', 'cursor-col-resize');
  };
  const onDividerPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!e.currentTarget.hasPointerCapture(e.pointerId)) return; // hover, not drag
    applyPointerPct(e.clientX);
  };
  const onDividerPointerEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    endDividerDrag(e.currentTarget, e.pointerId);
  };

  // Load PDF as blob (so we can pass through auth header)
  useEffect(() => {
    if (!id) return;
    let revokeUrl: string | null = null;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        // Forward active client header so the backend scopes correctly
        const activeClientJson = localStorage.getItem('activeClient');
        if (activeClientJson) {
          try {
            const client = JSON.parse(activeClientJson);
            if (client?.id) headers['X-Active-Client'] = client.id;
          } catch {}
        }
        const resp = await fetch(`${WORKER_API_BASE}/bank-statements/${id}/file`, {
          headers,
          credentials: 'include',
        });
        if (!resp.ok) {
          setPdfError(`Could not load PDF (HTTP ${resp.status})`);
          return;
        }
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        revokeUrl = url;
        if (!cancelled) setPdfUrl(url);
      } catch (e: any) {
        setPdfError(e?.message || 'Failed to load PDF');
      }
    })();
    return () => {
      cancelled = true;
      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
    };
  }, [id]);

  // Mutations
  const saveHeaderMut = useMutation({
    mutationFn: (body: Partial<StatementWithTx>) =>
      api(`/bank-statements/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bank-statement', id] });
      setHeaderEdits({});
    },
  });

  const saveTxMut = useMutation({
    mutationFn: ({ txId, body }: { txId: string; body: Partial<Transaction> }) =>
      api(`/bank-statements/transactions/${txId}`, { method: 'PATCH', body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bank-statement', id] });
    },
  });

  const deleteTxMut = useMutation({
    mutationFn: (txId: string) =>
      api(`/bank-statements/transactions/${txId}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bank-statement', id] });
    },
  });

  // Create a brand-new transaction row (used by the "Add Row" button)
  const createTxMut = useMutation({
    mutationFn: (body: Partial<Transaction>) =>
      api(`/bank-statements/${id}/transactions`, { method: 'POST', body }),
  });

  // ── Review queue (sessionStorage conveyor) — OBSOLETE 2026-11-04 ──
  // Retired in favour of the checklist-centric flow (Review Upload → detail →
  // back). Nothing writes the session queue anymore. Kept for revival knowledge:
  // function goNextInQueue() {
  //   const handoff = advanceReviewQueue(id);
  //   if (!handoff) return null;
  //   navigate(handoff.nextUrl ?? handoff.highlightUrl!);
  //   return true;
  // }
  function goNextInQueue() { return null; } // stub: call sites below kept intact

  const confirmMut = useMutation({
    mutationFn: (body?: any) => {
      return api(`/bank-statements/${id}/confirm`, { method: 'POST', body });
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['bank-statements'] });
      queryClient.invalidateQueries({ queryKey: ['bank-statements-drafts'] });
      queryClient.invalidateQueries({ queryKey: ['bank-continuity'] });
      toast.success(tr('Saved to database! This statement is now confirmed.', '已儲存至數據庫！此月結單已確認。', '已储存至数据库！此月结单已确认。'));
      setIsSaving(false);
      // Carousel retired 2026-11-04 — always return to the entry page.
      // const next = goNextInQueue(); if (!next) { navigate(exitDest(fromParam, '/bank-statements')); }
      navigate(exitDest(fromParam, '/bank-statements'));
    },
    onError: (err: any) => {
      toast.error(`Failed to save: ${err?.message || err?.error || 'Unknown error'}`);
      setIsSaving(false);
    },
  });

  const postMissingMut = useMutation({
    mutationFn: () => api(`/bank-statements/${id}/post-missing`, { method: 'POST' }),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['bank-statement', id] });
      queryClient.invalidateQueries({ queryKey: ['bank-lifecycle', id] });
      queryClient.invalidateQueries({ queryKey: ['bank-statements'] });
      queryClient.invalidateQueries({ queryKey: ['entries'] });
      if (data?.skipped_duplicate) {
        toast.info(tr(
          'Not posted: this file is already recorded by another statement.',
          '未過賬：此檔案已由另一張月結單記錄。',
          '未过账：此档案已由另一张月结单记录。',
        ));
      } else if ((data?.created || 0) > 0) {
        toast.success(tr(
          `${data.created} missing entr${data.created === 1 ? 'y' : 'ies'} posted.`,
          `已補記 ${data.created} 筆分錄。`,
          `已补记 ${data.created} 笔分录。`,
        ));
      } else {
        toast.info(tr('No missing entries to post.', '沒有需要補記的分錄。', '没有需要补记的分录。'));
      }
    },
    onError: (err: any) => {
      toast.error(err?.message || err?.error || tr('Failed to post missing entries', '補記分錄失敗', '补记分录失败'));
    },
  });

  const discardMut = useMutation({
    mutationFn: () => {
      return api(`/bank-statements/${id}`, { method: 'DELETE' });
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['bank-statements'] });
      queryClient.invalidateQueries({ queryKey: ['bank-statements-drafts'] });
      queryClient.invalidateQueries({ queryKey: ['bank-continuity'] });
      setIsSaving(false);
      // Carousel retired 2026-11-04 — always return to the entry page.
      // const next = goNextInQueue();
      // if (!next) { toast.success(...); navigate(exitDest(fromParam, '/bank-statements')); }
      toast.success(tr('Statement discarded.', '月結單已放棄。', '月结单已放弃。'));
      navigate(exitDest(fromParam, '/bank-statements'));
    },
    onError: (err: any) => {
      toast.error(`Failed to discard: ${err?.message || err?.error || 'Unknown error'}`);
      setIsSaving(false);
    },
  });

  const transactions = useMemo(
    () => [
      ...(stmt?.transactions || []).filter(t => !deletedTxIds.has(t.id)),
      ...localRows,
    ],
    [stmt, deletedTxIds, localRows]
  );

  const totals = useMemo(() => {
    const rowChecks: Record<string, { expected: number; actual: number | null; mismatch: boolean }> = {};
    const opening = Number(headerEdits.opening_balance ?? stmt?.opening_balance ?? 0);

    // Resolve each transaction's edited values once, preserving original (statement) order.
    const resolved = transactions.map(tx => {
      const e = txEdits[tx.id] || {};
      const date = e.transaction_date ?? tx.transaction_date ?? '';
      const description = e.description ?? tx.description ?? '';
      const deposit = Number(e.deposit_amount ?? tx.deposit_amount) || 0;
      const withdrawal = Number(e.withdrawal_amount ?? tx.withdrawal_amount) || 0;
      const shown = e.balance ?? tx.balance;
      const balance = shown != null ? Number(shown) : null;
      const accountType = (e.account_type ?? tx.account_type) || null;
      return { id: tx.id, date, description, deposit, withdrawal, balance, accountType };
    });

    const dep = resolved.reduce((s, t) => s + t.deposit, 0);
    const wit = resolved.reduce((s, t) => s + t.withdrawal, 0);

    // A statement can contain multiple sub-ledgers in one document (e.g. HSBC
    // "HKD Current" + "HKD Savings" sections), each with its own running balance.
    // Figure out how to split "resolved" into per-ledger segments:
    const distinctTypes = new Set(resolved.map(t => t.accountType).filter(Boolean));
    let segments: (typeof resolved)[];

    if (distinctTypes.size >= 2) {
      // Preferred: the parser reliably tagged account_type on multiple ledgers — group by it.
      const byType = new Map<string, typeof resolved>();
      for (const t of resolved) {
        const key = t.accountType || '__untagged__';
        if (!byType.has(key)) byType.set(key, []);
        byType.get(key)!.push(t);
      }
      segments = Array.from(byType.values());
    } else {
      // Fallback: account_type wasn't reliably tagged (the AI parse doesn't always
      // set it consistently). Detect ledger boundaries deterministically instead,
      // using "B/F BALANCE" (bring-forward) marker rows — these mark the start of
      // a new sub-account section in the ORIGINAL statement order. This does NOT
      // depend on the AI's tagging and works even when account_type is entirely absent.
      const isBfBalance = (desc: string) => /B\/?F\s*BAL/i.test(desc || '');
      segments = [];
      let current: typeof resolved = [];
      for (const t of resolved) {
        if (isBfBalance(t.description) && current.length > 0) {
          segments.push(current);
          current = [];
        }
        current.push(t);
      }
      if (current.length > 0) segments.push(current);
    }

    let computedClosing = 0;
    if (segments.length <= 1) {
      // Single-ledger statement — one continuous running balance from the header opening balance.
      const ordered = [...resolved].sort((a, b) => a.date.localeCompare(b.date));
      let running = opening;
      for (const t of ordered) {
        running = running + t.deposit - t.withdrawal;
        const mismatch = t.balance !== null && Math.abs(t.balance - running) > 0.01;
        rowChecks[t.id] = { expected: running, actual: t.balance, mismatch };
      }
      computedClosing = running;
    } else {
      // Multi sub-ledger statement — validate each ledger independently, anchored
      // on its own first row's stated balance (typically its "B/F BALANCE" row).
      // Sort WITHIN each segment only — segments themselves are never merged,
      // so dates that happen to coincide across different ledgers can't cross-contaminate.
      for (const segTxs of segments) {
        const ordered = [...segTxs].sort((a, b) => a.date.localeCompare(b.date));
        const first = ordered[0];
        const anchor = first.balance !== null
          ? first.balance - (first.deposit - first.withdrawal)
          : opening;
        let running = anchor;
        for (const t of ordered) {
          running = running + t.deposit - t.withdrawal;
          const mismatch = t.balance !== null && Math.abs(t.balance - running) > 0.01;
          rowChecks[t.id] = { expected: running, actual: t.balance, mismatch };
        }
        computedClosing += running;
      }
    }

    const declaredClosing = Number(headerEdits.closing_balance ?? stmt?.closing_balance ?? 0);
    const closingMismatch = Math.abs(computedClosing - declaredClosing) > 0.01;
    return {
      dep, wit, net: dep - wit,
      opening, computedClosing, declaredClosing, closingMismatch,
      rowChecks,
      mismatchCount: Object.values(rowChecks).filter(c => c.mismatch).length,
    };
  }, [transactions, txEdits, headerEdits, stmt]);

  // Fix Issue 4: when the user edits ANY transaction row, clear the
  // "closing manually edited" flag so the closing balance follows automatically.
  // The only time we keep the flag is when the user has typed directly into
  // the Closing Balance field AND has not subsequently edited any row.
  useEffect(() => {
    setClosingManuallyEdited(false);
  }, [txEdits]);

  // Auto-sync closing balance to the computed running total UNLESS the user has
  // manually typed a closing balance. This means when an accountant edits the
  // line items, the closing balance at the top follows automatically (Lily #4).
  useEffect(() => {
    if (closingManuallyEdited) return;
    if (!stmt) return;
    const current = headerEdits.closing_balance ?? stmt.closing_balance;
    // Only push an update if it actually differs, to avoid render loops
    if (current == null || Math.abs(Number(current) - totals.computedClosing) > 0.01) {
      setHeaderEdits(prev => ({ ...prev, closing_balance: totals.computedClosing }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totals.computedClosing, closingManuallyEdited]);

  if (isLoading) {
    return <div className="p-6 text-center text-muted-foreground">Loading extracted data…</div>;
  }
  if (!stmt) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-900 dark:text-red-200">Statement not found.</p>
        <Link to="/bank-statements" className="text-primary underline">← Back to Bank Statements</Link>
      </div>
    );
  }

  const lcStage = lc?.object?.stage;
  // Display stage: API-derived when available, else the raw status lane.
  const displayStage = stageOf('document', {
    lifecycle: lcStage ? { stage: lcStage } : null,
    status: stmt?.status ?? null,
  });
  const stageSince =
    lc?.object?.stage_since ??
    ((stmt as { updated_at?: string | null } | undefined)?.updated_at ?? undefined) ??
    undefined;
  // The doc's own review gate — from the row status only (derived stages from
  // child vouchers must not re-open a confirmed statement's editing flow).
  const ownStatus = stmt?.status ?? 'extracted';
  const preConfirm =
    ownStatus === 'needs_review' || ownStatus === 'extracted' || ownStatus === 'draft';
  const postingSummary = stmt?.posting_summary;
  const postingState = postingStatus(ownStatus, postingSummary);
  const merged = { ...stmt, ...headerEdits };
  const headerHasChanges = Object.keys(headerEdits).length > 0;
  const txDirtyCount = Object.keys(txEdits).length;

  const upd = (k: keyof StatementWithTx, v: any) =>
    setHeaderEdits(prev => ({ ...prev, [k]: v }));

  const saveHeader = () => {
    if (!headerHasChanges) return;
    saveHeaderMut.mutate(headerEdits);
  };

  const saveAllTxEdits = async () => {
    for (const [txId, rowBody] of Object.entries(txEdits)) {
      const body = rowBody as Partial<Transaction>;
      // Local rows (added manually) are created, not patched
      if (txId.startsWith('local-')) {
        const row = localRows.find(r => r.id === txId);
        if (row) {
          const combined = { ...row, ...body };
          await createTxMut.mutateAsync({
            transaction_date: combined.transaction_date,
            description: combined.description,
            deposit_amount: combined.deposit_amount,
            withdrawal_amount: combined.withdrawal_amount,
            balance: combined.balance,
            account_code: combined.account_code ?? null,
            payee_payer: combined.payee_payer ?? null,
            cheque_no: combined.cheque_no ?? null,
            match_status: combined.match_status ?? 'unmatched',
          });
        }
      } else {
        await saveTxMut.mutateAsync({ txId, body });
      }
    }
    // Create any local rows the user added but did not further edit
    for (const row of localRows) {
      if (!txEdits[row.id]) {
        await createTxMut.mutateAsync({
          transaction_date: row.transaction_date,
          description: row.description,
          deposit_amount: row.deposit_amount,
          withdrawal_amount: row.withdrawal_amount,
          balance: row.balance,
          account_code: row.account_code ?? null,
          payee_payer: row.payee_payer ?? null,
          cheque_no: row.cheque_no ?? null,
          match_status: row.match_status ?? 'unmatched',
        });
      }
    }
    setTxEdits({});
    setLocalRows([]);
  };

  const addRow = () => {
    const newId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const lastDate = transactions.length > 0
      ? (transactions[transactions.length - 1].transaction_date || merged.period_start || '')
      : (merged.period_start || '');
    setLocalRows(prev => [...prev, {
      id: newId,
      transaction_date: lastDate,
      description: '',
      deposit_amount: 0,
      withdrawal_amount: 0,
      balance: null,
      payee_payer: null,
      cheque_no: null,
    }]);
  };

  const saveAndConfirm = async () => {
    setIsSaving(true);
    try {
      if (headerHasChanges) {
        await saveHeaderMut.mutateAsync(headerEdits);
      }
      if (txDirtyCount > 0 || localRows.length > 0) {
        await saveAllTxEdits();
      }
    } catch (e: any) {
      toast.error(`Failed to save edits: ${e?.message || e?.error || 'Unknown error'}`);
      setIsSaving(false);
      return;
    }
    const status = totals.closingMismatch ? 'mismatch' : 'ok';
    const check = totals.closingMismatch ? {
      expected: totals.computedClosing,
      actual: totals.declaredClosing,
      diff: totals.declaredClosing - totals.computedClosing,
      corrected_by_user: closingManuallyEdited,
    } : null;
    confirmMut.mutate({ balance_status: status, balance_check: check });
  };

  // A bundled statement section starts at source_page (e.g. page 20 for the
  // April statement). Open the PDF there instead of always at page 1.
  const pdfSrc = withPdfPage(pdfUrl, stmt?.source_page);

  return (
    <div className="p-4 space-y-4 max-w-[1800px] mx-auto" key={id}>
      {/* Edit Transactions — jumps to the bank statements list with this
          statement's transaction record open in edit mode */}
      <div className="flex justify-end">
        <button
          onClick={() => navigate(`/bank-statements?statement=${id}&edit=1`)}
          className="px-3 py-1.5 border rounded-md text-sm font-medium hover:bg-muted"
          title={tr('Open this statement\'s transaction table in edit mode', '以編輯模式開啟此月結單的交易記錄', '以编辑模式开启此月结单的交易记录')}
        >
          {tr('Edit Transactions', '編輯交易記錄', '编辑交易记录')}
        </button>
      </div>
      {/* Duplicate notice — import refused to create a second copy of this file */}
      {isDuplicate && (
        <div className="rounded-lg border-2 border-orange-400 bg-orange-50 dark:bg-orange-950/40 p-4">
          <div className="flex items-start gap-3">
            <div className="text-2xl">🔁</div>
            <div>
              <h2 className="font-bold text-orange-800 dark:text-orange-200">
                {dupBlocked
                  ? tr('Already imported — showing the existing copy', '已導入過——正在顯示現有記錄', '已导入过——正在显示现有记录')
                  : tr('This statement has already been imported', '此月結單已導入過', '此月结单已导入过')}
              </h2>
              <p className="text-sm text-orange-700 dark:text-orange-300 mt-0.5">
                {dupBlocked
                  ? tr('This exact file was imported before, so nothing new was created. Review the existing records below, or discard the duplicate from the Bank Statements list.', '系統偵測到此文件之前已導入，未建立任何新數據。請在下方審核現有記錄，或於銀行月結單列表放棄重複項。', '系统侦测到此文件之前已导入，未创建任何新数据。请在下方审核现有记录，或于银行月结单列表放弃重复项。')
                  : dupStatus === 'deleted'
                  ? tr('This file was previously imported and later deleted. Saving will create a new record — journal entries will not be duplicated.', '此文件曾導入後被刪除。儲存將建立新記錄——不會重複記賬。', '此文件曾导入后被删除。储存将创建新记录——不会重复记账。')
                  : tr('A duplicate was detected. Please review before saving.', '檢測到重複。儲存前請先審核。', '检测到重复。储存前请先审核。')}
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Banner */}
      {preConfirm ? (
        <div className="rounded-lg border-2 border-yellow-400 bg-yellow-50 dark:bg-yellow-950 p-4">
          <div className="flex items-start gap-3">
            <div className="text-2xl">⚠️</div>
            <div className="flex-1">
              <h2 className="font-bold text-yellow-900 dark:text-yellow-100">
                {tr('Review extracted data before saving to database', '儲存至數據庫前請先審核提取的數據', '储存至数据库前請先审核提取的數據')}
              </h2>
              <p className="text-sm text-yellow-800 dark:text-yellow-200 mt-1">
                {i18n.language === 'en'
                  ? <>Compare the AI-extracted data on the right with the original PDF on the left. Edit any field that's wrong. When everything matches, click <strong>Save to Database</strong>.</>
                  : i18n.language === 'zh-Hans'
                  ? <>将右侧 AI 提取的数据与左侧原始 PDF 进行对比。修正任何错误，确认后点击<strong>储存至数据库</strong>。</>
                  : <>將右側 AI 提取的數據與左側原始 PDF 進行對比。修正任何錯誤，確認後點擊<strong>儲存至數據庫</strong>。</>}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-green-400 bg-green-50 dark:bg-green-950 p-3">
          <div className="flex items-center gap-2">
            <span className="text-xl">✅</span>
            <p className="text-sm text-green-900 dark:text-green-100">
              {i18n.language === 'en'
                ? <><strong>Confirmed.</strong> This statement is saved. Any edits below save instantly.</>
                : i18n.language === 'zh-Hans'
                ? <><strong>已确认。</strong>此月结单已储存。以下的编辑将即时保存。</>
                : <><strong>已確認。</strong>此月結單已儲存。以下的編輯將即時保存。</>}
            </p>
          </div>
        </div>
      )}

      {/* Lifecycle position — derived from the API's lifecycle.stage, shown near the header */}
      <div className="rounded-lg border bg-card px-4 py-2 flex items-center gap-3 overflow-x-auto">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold shrink-0">
          {tr('Lifecycle', '生命週期', '生命周期')}
        </span>
        <LifecycleStepper type="document" variant="full" stage={displayStage} stageSince={stageSince} onOpen={() => setDrillOpen(true)} />
        {/* Task 17: ⋯ → Send back for review — offered only while the raw doc
            status is still 'reviewed' (the endpoint 409s any other state;
            needs_review/voided docs must NOT offer it). */}
        <div className="ml-auto">
          <DocSendBackMenu
            type="bank_statement"
            id={id ?? ''}
            enabled={!!id && stmt?.status === 'reviewed'
              && !(stmt?.transactions || []).some((t: any) => t.payment_posting)}
            extraInvalidate={[
              ['bank-statement', id],
              ['bank-statements'],
              ['bank-statements-drafts'],
              ['bank-continuity'],
            ]}
          />
        </div>
      </div>
      <LifecycleDrilldown type="bank_statement" id={id ?? null} open={drillOpen} onClose={() => setDrillOpen(false)} />

      {/* Split-screen: PDF left, data right — divider is draggable on lg+
          (width % persisted in localStorage as bs-review-split). */}
      <div ref={splitRef} className="flex flex-col lg:flex-row gap-0 lg:gap-0" style={{ minHeight: '70vh', ['--pdf-w' as any]: `${pdfPct}%` }}>
        {/* Left: PDF viewer */}
        <div className="rounded-lg border bg-card flex flex-col w-full lg:w-[var(--pdf-w)] shrink-0">
          <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/30">
            <h3 className="font-bold text-sm">{tr('📄 Original Document', '📄 原始文件', '📄 原始文件')}</h3>
            <span className="text-xs text-muted-foreground truncate ml-2">{stmt.file_name || 'PDF'}</span>
          </div>
          <div className="flex-1 bg-muted/10 relative" style={{ minHeight: '70vh' }}>
            {pdfError ? (
              <div className="p-4 text-sm text-red-900 dark:text-red-200 text-center">
                {pdfError}
                <br/><span className="text-muted-foreground">You can still review and edit the extracted data on the right.</span>
              </div>
            ) : !pdfSrc ? (
              <div className="p-8 text-center text-sm text-muted-foreground">Loading PDF…</div>
            ) : (
              <iframe
                src={pdfSrc}
                title="Bank statement PDF"
                className="w-full h-full border-0"
                style={{ minHeight: '70vh' }}
              />
            )}
          </div>
        </div>

        {/* Drag handle (lg+ only) — pointer capture keeps the drag alive over
            the PDF iframe and ends it reliably wherever the button is released. */}
        <div
          onPointerDown={onDividerPointerDown}
          onPointerMove={onDividerPointerMove}
          onPointerUp={onDividerPointerEnd}
          onPointerCancel={onDividerPointerEnd}
          onLostPointerCapture={onDividerPointerEnd}
          role="separator"
          aria-orientation="vertical"
          aria-valuenow={Math.round(pdfPct)}
          aria-valuemin={20}
          aria-valuemax={80}
          className="hidden lg:flex w-2 shrink-0 cursor-col-resize touch-none items-center justify-center hover:bg-primary/20 active:bg-primary/30"
          title={tr('Drag to resize the PDF / details panes', '拖動以調整 PDF 與資料欄寬度', '拖动以调整 PDF 与资料栏宽度')}
        >
          <div className="h-10 w-0.5 rounded bg-border" />
        </div>

        {/* Right: Extracted data */}
        <div className="space-y-4 overflow-y-auto w-full lg:w-auto lg:flex-1 min-w-0 mt-4 lg:mt-0" style={{ maxHeight: '85vh' }}>
          {/* Header info */}
          <div className="rounded-lg border bg-card p-4">
            <h3 className="font-bold text-sm mb-3 flex items-center gap-2">
              {tr('📋 Extracted Statement Details', '📋 提取的月結單資料', '📋 提取的月结单资料')}
              {merged.ocr_source && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${ocrSourceBadgeClass(merged.ocr_source)}`}>
                  OCR: {ocrSourceLabel(merged.ocr_source)}
                </span>
              )}
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <Field label={tr('Bank Name', 'Bank Name 銀行名稱', 'Bank Name 银行名称')} value={merged.bank_name || ''} onChange={v => upd('bank_name', v)} />
              <Field label={tr('Account Number', 'Account Number 帳號', 'Account Number 账号')} value={merged.account_number || ''} onChange={v => upd('account_number', v)} />
              <Field label={tr('Branch', 'Branch 分行', 'Branch 分行')} value={merged.branch || ''} onChange={v => upd('branch', v)} />
              <Field label={tr('Currency', 'Currency 貨幣', 'Currency 货币')} value={merged.currency || ''} onChange={v => upd('currency', v)} />
              <Field
                label={tr('Account Type', 'Account Type 戶口類型', 'Account Type 户口类型')}
                value={merged.account_type || ''}
                onChange={v => upd('account_type', v)}
                placeholder={tr('e.g. Savings / Current / Foreign Currency', '例如：儲蓄 / 往來 / 外幣', '例如：储蓄 / 往来 / 外币')}
              />
              <Field label={tr('Period Start', 'Period Start 開始日期', 'Period Start 開始日期')} value={merged.period_start || ''} onChange={v => upd('period_start', v)} placeholder="YYYY-MM-DD" />
              <Field label={tr('Period End', 'Period End 結束日期', 'Period End 結束日期')} value={merged.period_end || ''} onChange={v => upd('period_end', v)} placeholder="YYYY-MM-DD" />
              <label className="block">
                <span className="text-xs text-muted-foreground">{tr('Opening Balance', 'Opening Balance 期初餘額', 'Opening Balance 期初余额')}</span>
                <MoneyInput
                  value={merged.opening_balance ?? null}
                  onChange={v => upd('opening_balance', v ?? 0)}
                  className="mt-1 block w-full px-2 py-1.5 bg-background border border-input rounded text-sm text-right"
                />
              </label>
              <label className="block">
                <span className="text-xs text-muted-foreground flex items-center justify-between">
                  <span>{tr('Closing Balance', 'Closing Balance 期末餘額', 'Closing Balance 期末余额')}</span>
                  <button
                    type="button"
                    onClick={() => { setClosingManuallyEdited(false); upd('closing_balance', totals.computedClosing); }}
                    className={`text-xs underline ${totals.closingMismatch ? 'text-blue-600 font-medium' : 'text-muted-foreground hover:text-blue-600'}`}
                    title={tr('Set to computed value (opening + deposits − withdrawals)', '設定為計算值（期初 + 存入 - 支出）', '设定为计算值（期初 + 存入 - 支出）')}
                  >
                    {totals.computedClosing !== 0 ? `= ${money(totals.computedClosing)}` : tr('auto-fill', '自動填寫', '自动填写')}
                  </button>
                </span>
                <MoneyInput
                  value={merged.closing_balance ?? null}
                  onChange={v => { setClosingManuallyEdited(true); upd('closing_balance', v ?? 0); }}
                  className="mt-1 block w-full px-2 py-1.5 bg-background border border-input rounded text-sm text-right"
                />
              </label>
            </div>
            {headerHasChanges && (
              <div className="mt-3 flex gap-2">
                <button
                  onClick={saveHeader}
                  disabled={saveHeaderMut.isPending}
                  className="px-3 py-1.5 bg-primary text-primary-foreground rounded text-xs"
                >
                  {saveHeaderMut.isPending ? (tr('Saving…', '儲存中…', '储存中…')) : (tr('💾 Save header changes', '💾 儲存標題修改', '💾 储存標題修改'))}
                </button>
                <button
                  onClick={() => setHeaderEdits({})}
                  className="px-3 py-1.5 border rounded text-xs hover:bg-muted"
                >
                  {tr('Discard', '放棄', '放弃')}
                </button>
              </div>
            )}
          </div>

          {/* Balance verification banner */}
          {(totals.closingMismatch || totals.mismatchCount > 0) && (
            <div className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-950/30 p-3 text-xs">
              <div className="font-bold text-red-800 dark:text-red-200 mb-1">⚠️ Balance discrepancy detected</div>
              {totals.closingMismatch && (
                <div className="text-red-700 dark:text-red-300">
                  Closing balance should be <span className="font-mono font-bold">{money(totals.computedClosing)}</span> based on
                  opening ({money(totals.opening)}) + deposits ({money(totals.dep)}) − withdrawals ({money(totals.wit)}),
                  but the statement shows <span className="font-mono font-bold">{money(totals.declaredClosing)}</span>.
                  Difference: <span className="font-mono font-bold">{money(totals.declaredClosing - totals.computedClosing)}</span>.
                  <button
                    type="button"
                    onClick={() => { setClosingManuallyEdited(false); upd('closing_balance', totals.computedClosing); }}
                    className="ml-2 bg-red-100 hover:bg-red-200 text-red-800 px-2 py-0.5 rounded border border-red-300"
                  >
                    Fix: use computed value
                  </button>
                </div>
              )}
              {totals.mismatchCount > 0 && (
                <div className="text-red-700 dark:text-red-300 mt-1">
                  {totals.mismatchCount} row{totals.mismatchCount === 1 ? '' : 's'} have a per-row balance that doesn't match the running total (highlighted below).
                </div>
              )}
            </div>
          )}
          {!totals.closingMismatch && totals.mismatchCount === 0 && transactions.length > 0 && (
            <div className="rounded-lg border border-green-300 bg-green-50 dark:bg-green-950/30 p-2 text-xs text-green-700 dark:text-green-300">
              ✓ Balance verified: opening + deposits − withdrawals = closing, and every row's balance matches the running total.
            </div>
          )}

          {/* Transactions */}
          <div className="rounded-lg border bg-card p-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-bold text-sm">💳 Transactions ({transactions.length})</h3>
              <div className="text-xs text-muted-foreground">
                <span className="text-green-900 dark:text-green-200 font-mono">+{money(totals.dep)}</span>
                <span className="mx-1">·</span>
                <span className="text-red-900 dark:text-red-200 font-mono">-{money(totals.wit)}</span>
                <span className="mx-1">·</span>
                <span className="font-mono font-bold">Net {money(totals.net)}</span>
              </div>
            </div>

            {transactions.length === 0 ? (
              <div className="py-6 text-center border-2 border-dashed rounded">
                <div className="text-3xl mb-2">📝</div>
                <div className="font-medium text-sm">No transactions were extracted</div>
                <div className="text-xs text-muted-foreground mt-1 mb-3">
                  The system could not read this file automatically. Enter the transactions manually below.
                </div>
                <button
                  onClick={addRow}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-medium hover:bg-primary/90"
                >
                  + Add first row
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs min-w-[1250px]">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="py-1 pr-1 font-medium w-28">{tr('Date', '日期', '日期')}</th>
                      <th className="py-1 pr-1 font-medium min-w-[220px]">{tr('Description', '描述', '描述')}</th>
                      <th className="py-1 pr-1 font-medium w-28">{tr('Payee / Payer', '收款人 / 付款人', '收款人 / 付款人')}</th>
                      <th className="py-1 pr-1 font-medium w-24">{tr('Cheque No.', '支票號碼', '支票号码')}</th>
                      <th className="py-1 pr-1 font-medium text-right w-24">{tr('Deposit', '存入', '存入')}</th>
                      <th className="py-1 pr-1 font-medium text-right w-24">{tr('Withdrawal', '提取', '提取')}</th>
                      <th className="py-1 pr-1 font-medium text-right w-24">{tr('Balance', '餘額', '余额')}</th>
                      <th className="py-1 pr-1 font-medium w-48">{tr('COA Account', '會計科目（借/貸）', '会计科目（借/贷）')}</th>
                      <th className="py-1 pr-1 font-medium w-32">{tr('Invoice', '發票', '发票')}</th>
                      <th className="py-1 font-medium w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map(tx => {
                      const e = txEdits[tx.id] || {};
                      const date = e.transaction_date ?? tx.transaction_date;
                      const desc = e.description ?? tx.description;
                      const dep = e.deposit_amount ?? tx.deposit_amount;
                      const wit = e.withdrawal_amount ?? tx.withdrawal_amount;
                      const bal = e.balance ?? tx.balance;
                      const dirty = !!txEdits[tx.id];
                      const check = totals.rowChecks[tx.id];
                      const mismatch = check?.mismatch;
                      const selectedCoa = (e.account_code ?? tx.account_code) || '';
                      const effMatchStatus = e.match_status ?? tx.match_status ?? null;
                      const effNa = effMatchStatus === 'not_required' && !selectedCoa;
                      const noCoaFlag = isMissingCoa({
                        amount: (dep || 0) > 0 ? (dep || 0) : -(wit || 0),
                        accountCode: selectedCoa,
                        matchStatus: effMatchStatus,
                      });
                      const upTx = (field: keyof Transaction, value: any) =>
                        setTxEdits(prev => ({ ...prev, [tx.id]: { ...prev[tx.id], [field]: value } }));
                      // COA toggle: picking an account makes a Not-Applicable row a
                      // real posting transaction; picking "Not-Applicable" marks it
                      // as non-posting. Other statuses (invoice confirmed/suggested)
                      // are left untouched.
                      const changeCoa = (code: string) => {
                        if (code) {
                          upTx('account_code', code);
                          if (effMatchStatus === 'not_required') upTx('match_status', 'unmatched');
                        } else {
                          upTx('account_code', null);
                          upTx('match_status', 'not_required');
                        }
                      };
                      const isLocal = tx.id.startsWith('local-');
                      // ── Dr/Cr account column (mirrors Bank Statements → view
                      // transaction record): posted rows show the voucher's real
                      // Dr/Cr pills; unposted rows get dual Dr/Cr pickers.
                      const postingView = rowPostingView(tx);
                      const reconciled = !!stmt.is_reconciled;
                      const isDep = (dep || 0) > 0;
                      const movement = isDep ? (dep || 0) : (wit || 0);
                      const stmtBankCode = merged.account_code || '';
                      const bankDefault = coaOptions.some(o => o.account_code === stmtBankCode) ? stmtBankCode : '';
                      // Posted bank-voucher rows: editing a side PATCHes/PUTs
                      // instantly (JEs regenerate server-side). Splits (>2 lines)
                      // and payment vouchers stay read-only here — their editor
                      // lives on the Bank Statements page.
                      const postedEditable = postingView.editable && !reconciled && postingView.lines.length === 2;
                      const changePostedLine = (line: any, newCode: string) => {
                        if (newCode === NOT_APPLICABLE) {
                          if (!window.confirm(tr(
                            'Mark this whole transaction as Not applicable? This will remove its posting.',
                            '將整筆交易標為不適用？這會移除其分錄。',
                            '将整笔交易标为不适用？这会移除其分录。',
                          ))) return;
                          saveTxMut.mutate({ txId: tx.id, body: { account_code: null, match_status: 'not_required' } });
                          return;
                        }
                        if (!newCode) return;
                        const lineIsDr = (Number(line.debit) || 0) > 0;
                        const isBankSide = isDep ? lineIsDr : !lineIsDr;
                        if (isBankSide && movement > 0) {
                          // Bank side change → rewrite the voucher with the explicit
                          // fixed bank account (same PUT the Bank Statements pickers use)
                          const contraLine = postingView.lines.find(l => l !== line) || line;
                          api(`/bank-statements/transactions/${tx.id}/posting`, {
                            method: 'PUT',
                            body: {
                              lines: [{ account_code: contraLine.account_code, amount: movement }],
                              movement_amount: movement,
                              fixed_account_code: newCode,
                            },
                          })
                            .then(() => queryClient.invalidateQueries({ queryKey: ['bank-statement', id] }))
                            .catch((err: any) => toast.error(err?.message || err?.error || tr('Save failed', '保存失敗', '保存失败')));
                        } else {
                          // Contra change → PATCH account_code (backend regenerates the JE)
                          saveTxMut.mutate({ txId: tx.id, body: { account_code: newCode } });
                        }
                      };
                      // Unposted rows: bank side writes the statement's own GL
                      // account (staged pre-confirm, instant after); contra side
                      // rides the existing staged edit (posts on confirm).
                      const changeStmtBank = (code: string) => {
                        if (preConfirm) upd('account_code', code);
                        else saveHeaderMut.mutate({ account_code: code });
                      };
                      const setUnpostedSide = (side: 'dr' | 'cr', v: string) => {
                        if (v === NOT_APPLICABLE) { changeCoa(''); return; }
                        if (!v) return;
                        const isBankSide = side === (isDep ? 'dr' : 'cr');
                        if (isBankSide) changeStmtBank(v);
                        else changeCoa(v);
                      };
                      const rowBg =
                        mismatch ? 'bg-red-50 dark:bg-red-950/30' :
                        isLocal ? 'bg-blue-50 dark:bg-blue-950/20' :
                        dirty ? 'bg-yellow-50 dark:bg-yellow-900/20' : '';
                      return (
                        <tr key={tx.id} className={`border-b ${rowBg} ${noCoaFlag ? 'border-l-4 border-l-amber-400' : ''}`}
                            title={mismatch ? `Balance mismatch: expected ${money(check.expected)} but shows ${money(check.actual)}` : noCoaFlag ? tr('No COA account assigned yet', '尚未指定會計科目', '尚未指定会计科目') : ''}>
                          <td className="py-1 pr-1">
                            <input
                              value={date || ''}
                              onChange={ev => upTx('transaction_date', ev.target.value)}
                              className="w-full px-1 py-0.5 bg-transparent border border-input rounded text-xs"
                            />
                          </td>
                          <td className="py-1 pr-1">
                            <input
                              value={desc || ''}
                              onChange={ev => upTx('description', ev.target.value)}
                              className="w-full px-1 py-0.5 bg-transparent border border-input rounded text-xs"
                            />
                            {effNa && (
                              <div className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1">
                                <span className="px-1 py-px rounded bg-muted border border-border font-medium">N/A</span>
                                {tr('Opening balance — no link or posting required', '期初結餘——無需連結或過賬', '期初结余——无需连结或过账')}
                              </div>
                            )}
                          </td>
                          <td className="py-1 pr-1">
                            <PayeeInput
                              value={e.payee_payer ?? tx.payee_payer ?? null}
                              onChange={v => upTx('payee_payer', v)}
                              placeholder={tr('Payee / payer', '收款人 / 付款人', '收款人 / 付款人')}
                            />
                          </td>
                          <td className="py-1 pr-1">
                            <PayeeInput
                              value={e.cheque_no ?? tx.cheque_no ?? null}
                              onChange={v => upTx('cheque_no', v)}
                              placeholder={tr('Cheque no.', '支票號碼', '支票号码')}
                            />
                          </td>
                          <td className="py-1 pr-1">
                            <MoneyInput
                              value={dep ?? 0}
                              onChange={v => upTx('deposit_amount', v ?? 0)}
                              className="w-full px-1 py-0.5 bg-transparent border border-input rounded text-xs text-right text-green-700"
                            />
                          </td>
                          <td className="py-1 pr-1">
                            <MoneyInput
                              value={wit ?? 0}
                              onChange={v => upTx('withdrawal_amount', v ?? 0)}
                              className="w-full px-1 py-0.5 bg-transparent border border-input rounded text-xs text-right text-red-700"
                            />
                          </td>
                          <td className="py-1 pr-1">
                            <MoneyInput
                              value={bal ?? null}
                              onChange={v => upTx('balance', v)}
                              className={`w-full px-1 py-0.5 bg-transparent border rounded text-xs text-right ${mismatch ? 'border-red-500 text-red-700' : 'border-input'}`}
                            />
                            {mismatch && (
                              <div className="text-[10px] text-red-900 dark:text-red-200 text-right mt-0.5">
                                should be {money(check.expected)}
                              </div>
                            )}
                          </td>
                          <td className="py-1 pr-1">
                            {postingView.lines.length > 0 ? (
                              /* Posted: real voucher lines as Dr/Cr pills. */
                              <div className="flex flex-col items-start gap-0.5">
                                {postingView.lines.map((l: any, i: number) => {
                                  const side = (Number(l.debit) || 0) > 0 ? 'Dr' : 'Cr';
                                  const amount = side === 'Dr' ? Number(l.debit) : Number(l.credit);
                                  const accName = coaOptions.find(o => o.account_code === l.account_code)?.account_name
                                    || l.account_name || '';
                                  const isTemp = isTemporaryAccount(accName);
                                  const title = `${side} ${l.account_code} · ${accName} · ${amount.toFixed(2)}`
                                    + (reconciled
                                      ? tr(' (reconciled — read-only)', '（已對賬——唯讀）', '（已对账——只读）')
                                      : postingView.source === 'payment'
                                      ? tr(' (recorded by invoice payment — edit on the Bank Statements page)', '（由發票付款記錄——請於銀行月結單頁面編輯）', '（由发票付款记录——请于银行月结单页面编辑）')
                                      : postingView.lines.length > 2
                                      ? tr(' (manual split — edit on the Bank Statements page)', '（手動拆分——請於銀行月結單頁面編輯）', '（手动拆分——请于银行月结单页面编辑）')
                                      : '');
                                  return (
                                    <PillSelect
                                      key={`${l.account_code}-${i}`}
                                      side={side}
                                      value={l.account_code}
                                      editable={postedEditable}
                                      coaOptions={coaOptions}
                                      onChange={v => changePostedLine(l, v)}
                                      title={title}
                                      tone={isTemp ? 'temp' : 'default'}
                                    />
                                  );
                                })}
                                {postingView.lines.length > 2 && (
                                  <span className="text-[9px] text-muted-foreground">
                                    {(tx.posting as any)?.entry_source === 'manual' ? tr('manual split', '手動拆分', '手动拆分') : tr('split', '拆分', '拆分')}
                                  </span>
                                )}
                              </div>
                            ) : effNa && !naRevealIds.has(tx.id) ? (
                              <button
                                onClick={() => setNaRevealIds(prev => new Set(prev).add(tx.id))}
                                className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded inline-flex items-center gap-1 hover:bg-muted/70 hover:text-foreground"
                                title={tr('Not applicable — click to assign Dr/Cr accounts', '不適用——點擊可指定借貸科目', '不适用——点击可指定借贷科目')}
                              >
                                {tr('Not applicable', '不適用', '不适用')}
                              </button>
                            ) : (
                              /* Unposted: dual Dr/Cr pickers — deposits Dr the bank
                                 account, withdrawals Cr it; leave the bank side on
                                 "— pick —" to auto-resolve from the bank name. */
                              <div
                                className="flex flex-col items-stretch gap-0.5"
                                title={tr('Pick the Dr and Cr accounts to post this row (bank side left empty = auto-resolve at confirm)', '選擇借貸科目以為此行記賬（銀行方留空＝確認時自動確定）', '选择借贷科目以为此行记账（银行方留空＝确认时自动确定）')}
                              >
                                <PillSelect
                                  side="Dr"
                                  value={isDep ? bankDefault : selectedCoa}
                                  editable={!reconciled}
                                  coaOptions={coaOptions}
                                  onChange={v => setUnpostedSide('dr', v)}
                                  title={isDep
                                    ? tr('Bank side (Dr) — leave empty to auto-resolve', '銀行方（借）——留空即自動確定', '银行方（借）——留空即自动确定')
                                    : tr('Contra account (Dr)', '對方科目（借）', '对方科目（借）')}
                                />
                                <PillSelect
                                  side="Cr"
                                  value={isDep ? selectedCoa : bankDefault}
                                  editable={!reconciled}
                                  coaOptions={coaOptions}
                                  onChange={v => setUnpostedSide('cr', v)}
                                  title={isDep
                                    ? tr('Contra account (Cr)', '對方科目（貸）', '对方科目（贷）')
                                    : tr('Bank side (Cr) — leave empty to auto-resolve', '銀行方（貸）——留空即自動確定', '银行方（贷）——留空即自动确定')}
                                />
                              </div>
                            )}
                          </td>
                          <td className="py-1 pr-1">
                            <InvoiceRefCell tx={tx} />
                          </td>
                          <td className="py-1 text-center">
                            <button
                              onClick={() => {
                                if (tx.id.startsWith('local-')) {
                                  // Local row — just remove from local state
                                  setLocalRows(prev => prev.filter(r => r.id !== tx.id));
                                  setTxEdits(prev => { const n = { ...prev }; delete n[tx.id]; return n; });
                                } else if (confirm('Delete this transaction?')) {
                                  setDeletedTxIds(prev => new Set(prev).add(tx.id));
                                  deleteTxMut.mutate(tx.id);
                                }
                              }}
                              className="text-red-900 dark:text-red-200 hover:text-red-800 text-xs"
                              title="Delete transaction"
                            >
                              🗑
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="border-t-2 border-gray-300 bg-muted/30">
                    <tr>
                      <td colSpan={4} className="py-2 pr-1 font-bold text-xs">
                        {transactions.length} transaction{transactions.length === 1 ? '' : 's'}
                      </td>
                      <td className="py-2 pr-1 text-right font-mono font-bold text-green-700">{money(totals.dep)}</td>
                      <td className="py-2 pr-1 text-right font-mono font-bold text-red-700">{money(totals.wit)}</td>
                      <td className="py-2 pr-1 text-right font-mono font-bold text-xs">
                        <div>Opening: {money(totals.opening)}</div>
                        <div>Closing: {money(totals.computedClosing)}</div>
                      </td>
                      <td className="py-2"></td>
                      <td className="py-2"></td>
                      <td className="py-2"></td>
                    </tr>
                  </tfoot>
                </table>
                <div className="mt-2 flex justify-end">
                  <button
                    onClick={addRow}
                    className="text-xs px-3 py-1.5 border border-blue-300 text-blue-700 rounded hover:bg-blue-50 dark:hover:bg-blue-900/30 font-medium"
                    title="Add a row that OCR may have missed"
                  >
                    + Add Row
                  </button>
                </div>
              </div>
            )}

            {txDirtyCount > 0 && (
              <div className="mt-3 flex gap-2 items-center">
                <button
                  onClick={saveAllTxEdits}
                  className="px-3 py-1.5 bg-primary text-primary-foreground rounded text-xs"
                >
                  💾 {tr(`Save ${txDirtyCount} transaction edit${txDirtyCount === 1 ? '' : 's'}`, `儲存 ${txDirtyCount} 筆交易修改`, `储存 ${txDirtyCount} 笔交易修改`)}
                </button>
                <button
                  onClick={() => setTxEdits({})}
                  className="px-3 py-1.5 border rounded text-xs hover:bg-muted"
                >
                  {tr('Discard changes', '放棄修改', '放弃修改')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer actions — in normal document flow at the bottom of the page */}
      <div className="rounded-lg border-2 border-primary bg-primary/5 p-4 mt-2">
        {preConfirm ? (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h3 className="font-bold text-sm">{tr('Ready to save?', '準備儲存？', '準備储存？')}</h3>
              <p className="text-xs text-muted-foreground">
                {i18n.language === 'en'
                  ? <>Click <strong>Save to Database</strong> to confirm this statement. You can still edit it later.</>
                  : i18n.language === 'zh-Hans'
                  ? <>点击<strong>储存至数据库</strong>确认此月结单。之后仍可编辑。</>
                  : <>點擊<strong>儲存至數據庫</strong>確認此月結單。之後仍可編輯。</>}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  if (isSaving || confirmMut.isPending || saveHeaderMut.isPending || createTxMut.isPending || discardMut.isPending) return;
                  const next = goNextInQueue();
                  if (!next) navigate(exitDest(fromParam, '/bank-statements'));
                }}
                disabled={isSaving || confirmMut.isPending || saveHeaderMut.isPending || createTxMut.isPending || discardMut.isPending}
                className="px-4 py-2 border rounded text-sm text-muted-foreground hover:bg-muted"
                title={tr('Go back without saving any changes', '不儲存任何變更並返回', '不储存任何变更并返回')}
              >
                {tr('← Back', '← 返回', '← 返回')}
              </button>
              <button
                onClick={() => {
                  if (confirm(tr('Discard this statement? It will be moved to the Recycle Bin where it can be restored within 30 days.', '放棄此月結單？將移至回收站，可在30天內還原。', '放弃此月结单？將移至回收站，可在30天內还原。'))) {
                    discardMut.mutate();
                  }
                }}
                disabled={discardMut.isPending}
                className="px-4 py-2 border border-red-300 text-red-900 dark:text-red-200 rounded text-sm hover:bg-red-50 dark:hover:bg-red-950"
              >
                {discardMut.isPending
                  ? (tr('Discarding…', '放棄中…', '放弃中…'))
                  : (tr('🗑 Discard', '🗑 放棄', '🗑 放弃'))}
              </button>
              <button
                onClick={saveAndConfirm}
                disabled={isSaving || confirmMut.isPending || saveHeaderMut.isPending || createTxMut.isPending || transactions.length === 0}
                className="px-6 py-2 bg-green-800 text-white rounded font-bold hover:bg-green-900 disabled:opacity-50 disabled:cursor-not-allowed"
                title={transactions.length === 0 ? (tr('Add at least one transaction before saving', '儲存前請先新增至少一筆交易', '储存前請先新增至少一笔交易')) : ''}
              >
                {isSaving || confirmMut.isPending
                  ? (tr('Confirming…', '確認中…', '确认中…'))
                  : (tr('✅ Confirm & Post to GL', '✅ 確認並過賬至總賬', '✅ 确认并过账至总账'))}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
            <p className="text-sm text-muted-foreground">
              {tr('This statement is already saved. Edits save instantly.', '此月結單已儲存。編輯將即時保存。', '此月结单已储存。編輯將即時保存。')}
            </p>
            {postingSummary && (
              <p className="text-xs text-muted-foreground mt-1">
                {tr(
                  `${postingSummary.posted_own} posted · ${postingSummary.posted_matched} recorded by invoice payment · ${postingSummary.no_posting} no posting needed · ${postingSummary.missing} missing`,
                  `${postingSummary.posted_own} 已過賬 · ${postingSummary.posted_matched} 已由發票付款記錄 · ${postingSummary.no_posting} 無需過賬 · ${postingSummary.missing} 待補記`,
                  `${postingSummary.posted_own} 已过账 · ${postingSummary.posted_matched} 已由发票付款记录 · ${postingSummary.no_posting} 无需过账 · ${postingSummary.missing} 待补记`,
                )}
              </p>
            )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {postingSummary && postingState === 'post_missing' && (
                <button
                  onClick={() => postMissingMut.mutate()}
                  disabled={postMissingMut.isPending}
                  className="px-4 py-2 bg-amber-600 text-white rounded text-sm font-medium hover:bg-amber-700 disabled:opacity-50"
                >
                  {postMissingMut.isPending
                    ? tr('Posting…', '過賬中…', '过账中…')
                    : tr(
                        `Post missing entries (${postingSummary?.missing || 0})`,
                        `補記缺少的分錄（${postingSummary?.missing || 0}）`,
                        `补记缺少的分录（${postingSummary?.missing || 0}）`,
                      )}
                </button>
              )}
              {postingSummary && postingState === 'posted' && (
                <span className="text-sm font-medium text-green-800 dark:text-green-300">
                  {(postingSummary?.posted_own || 0) > 0
                    ? tr('✓ Posted', '✓ 已過賬', '✓ 已过账')
                    : tr('✓ Recorded by invoice payment', '✓ 已由發票付款記錄', '✓ 已由发票付款记录')}
                </span>
              )}
              {postingSummary && postingState === 'nothing' && (
                <span className="text-sm text-muted-foreground">
                  {tr('Nothing to post', '無需過賬', '无需过账')}
                </span>
              )}
              <Link to={`/bank-statements?statement=${id}&focus=1`}
              className="px-4 py-2 bg-primary text-primary-foreground rounded text-sm"
              title={tr('Return to the list — reveals and highlights this statement, then opens its transaction record', '返回列表——將揭示並高亮此月結單，然後打開其交易記錄', '返回列表——将揭示并高亮此月结单，然后打开其交易记录')}
            >
              {tr('← Back to Bank Statements', '← 返回銀行月結單', '← 返回银行月结单')}
            </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Dr/Cr account pill — the posting-side counterpart of the Bank Statements
// transaction-record pills. Editable renders a borderless <select> inside the
// pill ("— pick —" empty, Not applicable, then the leaf COA accounts);
// read-only renders the account code + name with the full line in the tooltip.
function PillSelect({ side, value, editable, coaOptions, onChange, title, tone = 'default' }: {
  side: 'Dr' | 'Cr';
  value: string;
  editable?: boolean;
  coaOptions: { account_code: string; account_name: string }[];
  onChange: (v: string) => void;
  title?: string;
  tone?: 'default' | 'temp';
}) {
  const sideCls = side === 'Dr' ? 'text-blue-600 dark:text-blue-400' : 'text-orange-900 dark:text-orange-200';
  const pillCls = tone === 'temp'
    ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
    : value ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground';
  if (!editable) {
    const acct = coaOptions.find(o => o.account_code === value);
    return (
      <span className={`inline-flex items-center gap-1 max-w-full px-1.5 py-0.5 rounded text-[11px] font-mono ${pillCls}`} title={title}>
        <span className={`text-[10px] font-semibold shrink-0 ${sideCls}`}>{side}</span>
        <span className="truncate">{value}{acct ? ` ${acct.account_name}` : ''}</span>
      </span>
    );
  }
  return (
    <label className={`inline-flex items-center gap-1 w-full px-1.5 py-0.5 rounded text-[11px] font-mono cursor-pointer ${pillCls}`} title={title}>
      <span className={`text-[10px] font-semibold shrink-0 ${sideCls}`}>{side}</span>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="bg-transparent border-0 p-0 m-0 text-[11px] font-mono outline-none w-full min-w-0 cursor-pointer truncate"
      >
        <option value="">{tr('— pick —', '— 選 —', '— 选 —')}</option>
        <option value={NOT_APPLICABLE}>{tr('Not applicable', '不適用', '不适用')}</option>
        {coaOptions.map(a => (
          <option key={a.account_code} value={a.account_code}>{a.account_code} {a.account_name}</option>
        ))}
      </select>
    </label>
  );
}

// Payee/payer + cheque no. input (always editable on the review page).
// NULL = blank (type a value), 'N/A' = explicitly not applicable, any other
// string = the value — LLM-extracted when the statement prints it.
function PayeeInput({ value, onChange, placeholder }: {
  value: string | null | undefined;
  onChange: (v: string | null) => void;
  placeholder?: string;
}) {
  const na = value === 'N/A';
  return (
    <div className="flex items-center gap-1">
      {na ? (
        <span className="flex-1 text-center text-[10px] px-1.5 py-1 rounded bg-muted border border-border font-medium text-muted-foreground">N/A</span>
      ) : (
        <input
          value={value || ''}
          placeholder={placeholder || '—'}
          onChange={e => onChange(e.target.value.trim() || null)}
          className="w-full min-w-0 px-1 py-0.5 bg-transparent border border-input rounded text-xs"
        />
      )}
      <button
        type="button"
        onClick={() => onChange(na ? null : 'N/A')}
        title={na
          ? tr('Clear Not-Applicable — type a value instead', '清除不適用——改為輸入值', '清除不适用——改为输入值')
          : tr('Mark as not applicable', '標記為不適用', '标记为不适用')}
        className={`shrink-0 px-1 py-0.5 rounded text-xs border font-semibold ${
          na ? 'text-primary border-primary/40 bg-primary/5' : 'text-muted-foreground border-border hover:bg-muted'
        }`}
      >Ø</button>
    </div>
  );
}

// Invoice column cell — READ-ONLY mirror of the Bank Statements "Linked
// Document" badges (BankStatements.tsx :1633-1724) minus every action: link/
// unlink/confirm stay on /bank-statements; this screen only shows what is
// already linked so reviewers can see match state while checking OCR output.
function InvoiceRefCell({ tx }: { tx: Transaction }) {
  const linkedInvoices = tx.linked_invoices || [];
  const extraLinkedInvoices = linkedInvoices.filter(li => li.invoice_number !== tx.invoice_number);
  const hasLinkedDocument = !!tx.invoice_number || !!tx.card_statement_id || linkedInvoices.length > 0;
  const suggested = tx.match_status === 'suggested';
  return (
    <div className="flex flex-col items-start gap-0.5" data-testid="review-invoice-cell">
      {tx.invoice_number && (
        <span
          data-testid="invoice-reference-badge"
          title={suggested
            ? tr('Suggested match — confirm or reject on the Bank Statements page', '建議配對——請於銀行月結單頁面確認', '建议配对——请于银行月结单页面确认')
            : tr(`Invoice ${tx.invoice_number}${tx.invoice_total != null ? ` · ${money(tx.invoice_total)}` : ''}`, `發票 ${tx.invoice_number}`, `发票 ${tx.invoice_number}`)}
          className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded ${
            suggested
              ? 'text-yellow-900 dark:text-yellow-200 bg-yellow-100 dark:bg-yellow-900/30'
              : 'text-green-700 bg-green-100 dark:bg-green-900/30'
          }`}
        >
          {tx.invoice_number}
        </span>
      )}
      {extraLinkedInvoices.slice(0, 2).map(li => (
        <span
          key={li.invoice_id || li.invoice_number}
          data-testid="invoice-reference-badge"
          className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded text-green-700 bg-green-100 dark:bg-green-900/30"
        >
          <span className="font-medium">{li.invoice_number}</span>
          {li.allocated_amount != null && (
            <span className="text-muted-foreground font-mono">{money(li.allocated_amount)}</span>
          )}
        </span>
      ))}
      {linkedInvoices.length > 2 && (
        <span
          className="inline-flex items-center px-1.5 py-0.5 rounded bg-muted text-[10px] font-medium text-muted-foreground"
          title={linkedInvoices.map(li => li.invoice_number).join(', ')}
        >
          +{linkedInvoices.length - 2}
        </span>
      )}
      {tx.card_statement_id && !tx.invoice_number && (
        <span className="inline-flex items-center gap-1 text-xs text-purple-700 bg-purple-100 dark:bg-purple-900/30 px-2 py-0.5 rounded">
          {tx.card_issuer || tr('Card statement', '信用卡月結單', '信用卡月结单')}
        </span>
      )}
      {tx.match_status === 'not_required' && !hasLinkedDocument && (
        <span
          className="inline-flex items-center gap-1 text-muted-foreground"
          title={tr('No link or posting required', '無需連結或過賬', '无需连结或过账')}
        >
          <span className="px-1 py-px rounded bg-muted border border-border font-medium text-[10px]">N/A</span>
        </span>
      )}
      {!hasLinkedDocument && tx.match_status !== 'skipped' && tx.match_status !== 'not_required' && (
        <span className="text-muted-foreground" aria-label={tr('No linked invoice', '沒有連結發票', '没有连结发票')}>—</span>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', placeholder }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string;
}) {
  return (
    <label className="block">
      <span className="text-xs text-muted-foreground">{label}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        className="mt-0.5 block w-full px-2 py-1.5 bg-background border border-input rounded text-xs"
      />
    </label>
  );
}
